/* eslint-disable no-useless-escape */
import React from "react";
import { locales } from "../../locales";
import { More } from "../../assets";
import { getDataSource } from "../../services/api";
import CandidateImage from "./CandidateImage";
import PartyLogo from "./PartyLogo";
import { formatNumber, imageNameFormat } from "../../utils/communesUtils";

const CandidatesElected = ({
  data,
  langId,
  isDesktop,
  isBigCommune,
  currentElected,
  year,
  candidatesLookup,
  onShowTooltip,
  onHideTooltip,
  communeId,
}) => {
  // format string. remove accents, replace spaces with dashes and lowercase
  function formatString(string) {
    return string
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9]/g, "");
  }

  function normalizeAndSortName(name) {
    // Map of accent characters and their non-accented counterparts
    const accentsMap = {
      á: "a",
      ã: "a",
      à: "a",
      â: "a",
      é: "e",
      ẽ: "e",
      è: "e",
      ê: "e",
      í: "i",
      ĩ: "i",
      ì: "i",
      î: "i",
      ó: "o",
      õ: "o",
      ò: "o",
      ô: "o",
      ú: "u",
      ũ: "u",
      ù: "u",
      û: "u",
      ç: "c",
    };

    // Make lowercase, remove punctuation, split into parts, sort and join again
    return name
      .toLowerCase()
      .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "")
      .replace(/[\u00C0-\u017F]/g, (char) => accentsMap[char] || char) // Handle accent removal
      .trim()
      .split(" ")
      .sort()
      .join(" ");
  }

  function findCandidateByName(name, attribute) {
    if (!candidatesLookup) {
      return locales[langId].noDataAvailable;
    }

    const normalizedInputName = normalizeAndSortName(name);

    for (let party of candidatesLookup) {
      let candidate = party.candidates.find((candidate) => {
        return normalizeAndSortName(candidate.name) === normalizedInputName;
      });

      if (candidate) {
        return candidate[attribute];
      }
    }

    return locales[langId].noDataAvailable;
  }

  function findCandidateDetailsByName(name) {
    if (!candidatesLookup) {
      return locales[langId].noDataAvailable;
    }
    const normalizedInputName = normalizeAndSortName(name);

    for (let party of candidatesLookup) {
      let candidate = party.candidates.find((candidate) => {
        return normalizeAndSortName(candidate.name) === normalizedInputName;
      });

      if (candidate) {
        return candidate;
      }
    }

    return locales[langId].noDataAvailable;
  }

  function positionFormat(item) {
    let gender = findCandidateByName(item.contenu, "gender");
    if (gender === "Frau" || gender === "Femme" || gender === "Mulher") {
      return locales[langId].womenCouncillor;
    }
    return locales[langId].councillor;
  }

  const isNewResults = year === 2023;

  return (
    <div className="wort-small:grid wort-small:grid-cols-2 wort-small:gap-3 wort-medium:grid-cols-3">
      {data.map((item) => {
        let partyLogoUrl;
        const candidateDetails = findCandidateDetailsByName(item.contenu);
        const candidateImage = (item) => {
          if (
            findCandidateByName(item.contenu, "imageWebp") ===
            "Nicht mitgeteilt"
          ) {
            const tempImageName =
              formatString(item.sigle ? item.sigle : "candidates") +
              "/" +
              imageNameFormat(
                item.contenu.split(" ")[1],
                item.contenu.split(" ")[0]
              ) +
              ".webp";
            return tempImageName;
          }
          return findCandidateByName(item.contenu, "imageWebp");
        };
        if (
          (communeId === "33" && item.sigle === "Är Equipe") ||
          (communeId === "47" && item.sigle === "ÄR EQUIPE")
        ) {
          partyLogoUrl = `${getDataSource()}/parties/arequipe_33.svg`;
        } else if (communeId === "9" && item.sigle === "AR LEIT") {
          partyLogoUrl = `${getDataSource()}/parties/bissen_arleit.svg`;
        } else {
          partyLogoUrl = `${getDataSource()}/parties/${formatString(
            item.sigle ? item.sigle : "candidate"
          )}.svg`;
        }

        return (
          <div
            className="px-2 pt-2.5 flex flex-row items-start rounded-md bg-wort-background min-h-[100px] mb-3 wort-medium:mb-0 pb-2"
            // style={boxStyle(isDesktop)}
            key={item.contenu}
          >
            <CandidateImage
              imageUrl={`${getDataSource()}/portraits/${candidateImage(item)}`}
              imageUrlFallback={`${getDataSource()}/portraits/${findCandidateByName(
                item.contenu,
                "image"
              )}`}
            />
            <div className="flex flex-col items-start w-full">
              <div
                className="flex flex-row justify-between w-full mb-1"
                style={{ minHeight: 28 }}
              >
                <span className="font-bold leading-6" style={{ fontSize: 18 }}>
                  {item.contenu}
                </span>
                {isBigCommune && (
                  <PartyLogo
                    imageUrl={partyLogoUrl}
                    imageUrlFallback={`${getDataSource()}/parties/candidate.svg`}
                    party={item.sigle ? formatString(item.sigle) : ""}
                    langId={langId}
                  />
                )}
              </div>
              <hr style={{ width: "100%" }} />
              <div className="grid grid-cols-1 gap-1 mt-2">
                {isNewResults && (
                  <>
                    <div className="flex flex-row">
                      <span className="text-xs mr-2">
                        {locales[langId].age}:
                      </span>
                      <span className="text-xs font-semibold">
                        {findCandidateByName(item.contenu, "age")
                          ? findCandidateByName(item.contenu, "age")
                          : locales[langId].noDataAvailable}
                      </span>
                    </div>
                    <div className="flex flex-row">
                      <span className="text-xs mr-2">
                        {locales[langId].function}:
                      </span>
                      <span className="text-xs font-semibold">
                        {item.typeMandat
                          ? positionFormat(item)
                          : locales[langId].noDataAvailable}
                      </span>
                    </div>
                    <div className="flex flex-row items-center">
                      <span className="text-xs mr-2">
                        {locales[langId].occupation}:
                      </span>
                      <p
                        className="text-xs font-semibold"
                        style={{ lineHeight: "14px" }}
                      >
                        {findCandidateByName(item.contenu, "occupation")
                          ? findCandidateByName(item.contenu, "occupation")
                          : locales[langId].noDataAvailable}
                      </p>
                    </div>
                    <div className="flex flex-row">
                      <span className="text-xs mr-2">
                        {locales[langId].firstMandate}:
                      </span>
                      <span className="text-xs font-semibold">
                        {findCandidateByName(item.contenu, "firstMandate")
                          ? findCandidateByName(item.contenu, "firstMandate")
                          : locales[langId].noDataAvailable}
                      </span>
                    </div>
                  </>
                )}
                <div className="flex flex-row">
                  <span className="text-xs mr-2">{locales[langId].votes}:</span>
                  <span className="text-xs font-semibold">
                    {item.suffrages
                      ? formatNumber(item.suffrages)
                      : locales[langId].noDataAvailable}
                  </span>
                </div>
              </div>
              {isNewResults && (
                <div className="flex flex-row justify-end w-full">
                  <button
                    onClick={(event) =>
                      onShowTooltip(event, {
                        ...candidateDetails,
                        votesReceived2023: parseInt(item.suffrages),
                      })
                    }
                    onBlur={onHideTooltip}
                    className="focus:outline-none"
                  >
                    <More />
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CandidatesElected;
